import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface DebugInfo {
  storageAvailable: boolean;
  sessionStorageAvailable: boolean;
  userAgent: string;
  timestamp: string;
}

export function DebugOverlay() {
  const { user, session, loading } = useAuth();
  const [show, setShow] = useState(false);
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);

  useEffect(() => {
    // Check URL for debug parameter
    const params = new URLSearchParams(window.location.search);
    if (params.get('debug') === 'true') {
      setShow(true);
    }
  }, []);

  useEffect(() => {
    if (!show) return;

    const testStorage = (storage: Storage | null, name: string): boolean => {
      if (!storage) return false;
      try {
        const testKey = `__${name}_test__`;
        storage.setItem(testKey, 'test');
        storage.removeItem(testKey);
        return true;
      } catch {
        return false;
      }
    };

    setDebugInfo({
      storageAvailable: testStorage(typeof localStorage !== 'undefined' ? localStorage : null, 'localStorage'),
      sessionStorageAvailable: testStorage(typeof sessionStorage !== 'undefined' ? sessionStorage : null, 'sessionStorage'),
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    });
  }, [show]);

  if (!show) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-black/90 text-white text-xs p-3 z-[9999] max-h-[40vh] overflow-auto">
      <div className="flex justify-between items-center mb-2">
        <span className="font-bold text-yellow-400">🔧 Debug Mode</span>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setShow(false)}
          className="h-6 w-6 p-0 text-white hover:bg-white/20"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
      
      <div className="space-y-1 font-mono">
        <div className="flex gap-2">
          <span className="text-gray-400">Auth Loading:</span>
          <span className={loading ? 'text-yellow-400' : 'text-green-400'}>
            {loading ? 'true ⏳' : 'false ✓'}
          </span>
        </div>
        
        <div className="flex gap-2">
          <span className="text-gray-400">User:</span>
          <span className={user ? 'text-green-400' : 'text-red-400'}>
            {user ? `${user.email || user.phone || user.id.slice(0, 8)}...` : 'null'}
          </span>
        </div>
        
        <div className="flex gap-2">
          <span className="text-gray-400">Session:</span>
          <span className={session ? 'text-green-400' : 'text-red-400'}>
            {session ? 'active ✓' : 'null'}
          </span>
        </div>

        {debugInfo && (
          <>
            <div className="flex gap-2">
              <span className="text-gray-400">localStorage:</span>
              <span className={debugInfo.storageAvailable ? 'text-green-400' : 'text-red-400'}>
                {debugInfo.storageAvailable ? 'available ✓' : 'blocked ✗'}
              </span>
            </div>
            
            <div className="flex gap-2">
              <span className="text-gray-400">sessionStorage:</span>
              <span className={debugInfo.sessionStorageAvailable ? 'text-green-400' : 'text-red-400'}>
                {debugInfo.sessionStorageAvailable ? 'available ✓' : 'blocked ✗'}
              </span>
            </div>

            <div className="mt-2 text-gray-500 break-all">
              UA: {debugInfo.userAgent.slice(0, 100)}...
            </div>
          </>
        )}
      </div>
    </div>
  );
}
